import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';

// ---- Color conversion: HEX <-> RGB <-> HSV <-> HSL (no external dependencies) ----
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const hexToRgb = (hex: string) => {
  const normalized = hex.replace('#', '');
  const full = normalized.length === 3 ? normalized.split('').map((c) => c + c).join('') : normalized;
  const int = Number.parseInt(full, 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
};

const rgbToHex = (r: number, g: number, b: number) => {
  const toHex = (n: number) => clamp(Math.round(n), 0, 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
};

const rgbToHsv = (r: number, g: number, b: number) => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : (d / max) * 100, v: max * 100 };
};

const getHueSectorRgb = (h: number, c: number, x: number): [number, number, number] => {
  if (h < 60) return [c, x, 0];
  if (h < 120) return [x, c, 0];
  if (h < 180) return [0, c, x];
  if (h < 240) return [0, x, c];
  if (h < 300) return [x, 0, c];
  return [c, 0, x];
};

const hsvToRgb = (h: number, s: number, v: number) => {
  s /= 100;
  v /= 100;
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  const [r, g, b] = getHueSectorRgb(h, c, x);
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
};

const rgbToHsl = (r: number, g: number, b: number) => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: s * 100, l: l * 100 };
};

const isValidHex = (value: string) => /^#([0-9a-f]{3}){1,2}$/i.test(value);

const MAX_SAVED_COLORS = 12;
const STORAGE_KEY = 'canvas-color-picker-saved-colors';
type ColorFormat = 'hex' | 'rgb' | 'hsl';
const NEXT_FORMAT: Record<ColorFormat, ColorFormat> = { hex: 'rgb', rgb: 'hsl', hsl: 'hex' };
const SAVED_COLOR_SLOTS = Array.from({ length: MAX_SAVED_COLORS }, (_, slot) => `slot-${slot}`);

const formatColor = (
  format: ColorFormat,
  hex: string,
  rgb: { r: number; g: number; b: number },
  hsl: { h: number; s: number; l: number }
) => {
  if (format === 'hex') return hex;
  if (format === 'rgb') return `rgb(${Math.round(rgb.r)}, ${Math.round(rgb.g)}, ${Math.round(rgb.b)})`;
  return `hsl(${Math.round(hsl.h)}, ${Math.round(hsl.s)}%, ${Math.round(hsl.l)}%)`;
};

const readSavedColors = (): string[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

interface ColorPickerInputProps {
  value: string;
  onChange: (color: string) => void;
  className?: string;
}

// Custom color popover (Compass doesn't provide one): saturation/value square,
// hue bar, hex/rgb/hsl format selector and a palette of saved colors (LIFO).
const ColorPickerInput = ({ value, onChange, className }: ColorPickerInputProps) => {
  const currentHex = isValidHex(value) ? value : '#FFFFFF';
  const initialRgb = hexToRgb(currentHex);

  const [isOpen, setIsOpen] = useState(false);
  const [format, setFormat] = useState<ColorFormat>('hex');
  const [hsv, setHsv] = useState(() => rgbToHsv(initialRgb.r, initialRgb.g, initialRgb.b));
  const [savedColors, setSavedColors] = useState<string[]>(readSavedColors);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const satBoxRef = useRef<HTMLDivElement>(null);
  const hueBarRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<'sat' | 'hue' | null>(null);
  const hsvRef = useRef(hsv);

  useEffect(() => {
    hsvRef.current = hsv;
  }, [hsv]);

  // Only resyncs from the external value when the popover is closed,
  // so it doesn't fight the changes the picker itself generates while dragging.
  useEffect(() => {
    if (isOpen) return;
    const rgb = hexToRgb(currentHex);
    setHsv(rgbToHsv(rgb.r, rgb.g, rgb.b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentHex, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const applyHsv = (next: { h: number; s: number; v: number }) => {
    setHsv(next);
    const rgb = hsvToRgb(next.h, next.s, next.v);
    onChange(rgbToHex(rgb.r, rgb.g, rgb.b));
  };

  const updateFromSatBox = (clientX: number, clientY: number) => {
    const box = satBoxRef.current;
    if (!box) return;
    const rect = box.getBoundingClientRect();
    const x = clamp(clientX - rect.left, 0, rect.width);
    const y = clamp(clientY - rect.top, 0, rect.height);
    applyHsv({ h: hsvRef.current.h, s: (x / rect.width) * 100, v: 100 - (y / rect.height) * 100 });
  };

  const updateFromHueBar = (clientX: number) => {
    const bar = hueBarRef.current;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    const x = clamp(clientX - rect.left, 0, rect.width);
    applyHsv({ h: (x / rect.width) * 360, s: hsvRef.current.s, v: hsvRef.current.v });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (draggingRef.current === 'sat') updateFromSatBox(e.clientX, e.clientY);
      else if (draggingRef.current === 'hue') updateFromHueBar(e.clientX);
    };
    const handleMouseUp = () => {
      draggingRef.current = null;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rgb = hsvToRgb(hsv.h, hsv.s, hsv.v);
  const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

  const formattedValue = formatColor(format, hex, rgb, hsl);

  const handleHexTextChange = (text: string) => {
    if (!isValidHex(text)) return;
    const parsed = hexToRgb(text);
    applyHsv(rgbToHsv(parsed.r, parsed.g, parsed.b));
  };

  const cycleFormat = () => {
    setFormat((prev) => NEXT_FORMAT[prev]);
  };

  const handleSatBoxKeyDown = (e: ReactKeyboardEvent) => {
    const step = e.shiftKey ? 10 : 1;
    const { h, s, v } = hsvRef.current;
    const deltas: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, step],
      ArrowDown: [0, -step],
    };
    const delta = deltas[e.key];
    if (!delta) return;
    e.preventDefault();
    applyHsv({ h, s: clamp(s + delta[0], 0, 100), v: clamp(v + delta[1], 0, 100) });
  };

  const handleHueBarKeyDown = (e: ReactKeyboardEvent) => {
    const step = e.shiftKey ? 10 : 1;
    const { h, s, v } = hsvRef.current;
    let delta = 0;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') delta = -step;
    else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') delta = step;
    else return;
    e.preventDefault();
    applyHsv({ h: clamp(h + delta, 0, 360), s, v });
  };

  const handleAddSavedColor = () => {
    setSavedColors((prev) => {
      // LIFO: the new color goes first; if the list is full, the last one is dropped.
      const next = [hex, ...prev.filter((c) => c.toLowerCase() !== hex.toLowerCase())].slice(0, MAX_SAVED_COLORS);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // localStorage can fail in private mode; not critical for the picker.
      }
      return next;
    });
  };

  const handleSelectSaved = (savedHex: string) => {
    const parsed = hexToRgb(savedHex);
    applyHsv(rgbToHsv(parsed.r, parsed.g, parsed.b));
  };

  const handleToggleOpen = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPopoverPosition({ top: rect.bottom + 4, left: rect.left });
    }
    setIsOpen((prev) => !prev);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggleOpen}
        title="Elegir color"
        style={{ backgroundColor: currentHex }}
        className={`shrink-0 rounded-md border border-gray-200 cursor-pointer ${className ?? 'w-16 h-8'}`}
      />

      {isOpen &&
        createPortal(
          <div
            ref={popoverRef}
            style={{ top: popoverPosition.top, left: popoverPosition.left }}
            className="fixed z-50 w-72 p-4 bg-white rounded-lg shadow-xl border border-gray-200 flex flex-col gap-3"
          >
          <div
            ref={satBoxRef}
            role="slider"
            tabIndex={0}
            aria-label="Saturación y brillo"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(hsv.s)}
            onKeyDown={handleSatBoxKeyDown}
            onMouseDown={(e) => {
              draggingRef.current = 'sat';
              updateFromSatBox(e.clientX, e.clientY);
            }}
            className="relative w-full h-40 rounded-md cursor-crosshair"
            style={{
              backgroundColor: `hsl(${hsv.h}, 100%, 50%)`,
              backgroundImage:
                'linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent)',
            }}
          >
            <span
              className="absolute w-3 h-3 -ml-1.5 -mt-1.5 rounded-full border-2 border-white shadow pointer-events-none"
              style={{ left: `${hsv.s}%`, top: `${100 - hsv.v}%`, backgroundColor: hex }}
            />
          </div>

          <div
            ref={hueBarRef}
            role="slider"
            tabIndex={0}
            aria-label="Matiz"
            aria-valuemin={0}
            aria-valuemax={360}
            aria-valuenow={Math.round(hsv.h)}
            onKeyDown={handleHueBarKeyDown}
            onMouseDown={(e) => {
              draggingRef.current = 'hue';
              updateFromHueBar(e.clientX);
            }}
            className="relative w-full h-3 rounded-full cursor-pointer"
            style={{
              background: 'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
            }}
          >
            <span
              className="absolute top-1/2 w-4 h-4 -ml-2 -mt-2 rounded-full border-2 border-white shadow pointer-events-none"
              style={{ left: `${(hsv.h / 360) * 100}%`, backgroundColor: `hsl(${hsv.h}, 100%, 50%)` }}
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={cycleFormat}
              className="h-8 px-3 text-sm font-normal text-gray-600 border border-gray-200 rounded-md cursor-pointer uppercase"
            >
              {format}
            </button>
            <input
              value={formattedValue}
              onChange={(e) => handleHexTextChange(e.target.value)}
              readOnly={format !== 'hex'}
              className="flex-1 h-8 px-2 text-sm font-normal text-gray-600 border border-gray-200 rounded-md"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">Saved colors:</span>
              <button
                type="button"
                onClick={handleAddSavedColor}
                className="text-xs font-medium text-gray-700 cursor-pointer"
              >
                + Add
              </button>
            </div>
            <div className="grid grid-cols-6 gap-2">
              {SAVED_COLOR_SLOTS.map((slotId, index) => {
                const savedHex = savedColors[index];
                const isActive = !!savedHex && savedHex.toLowerCase() === hex.toLowerCase();
                return savedHex ? (
                  <button
                    key={slotId}
                    type="button"
                    onClick={() => handleSelectSaved(savedHex)}
                    style={{ backgroundColor: savedHex }}
                    className={`w-7 h-7 rounded-full border cursor-pointer ${
                      isActive ? 'ring-2 ring-gray-400 border-white' : 'border-gray-200'
                    }`}
                  />
                ) : (
                  <span key={slotId} className="w-7 h-7 rounded-full border border-dashed border-gray-200" />
                );
              })}
            </div>
          </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default ColorPickerInput;
